import { Input } from "@babackapp/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { Switch } from "@babackapp/ui/components/switch";
import { getFieldById, getFieldOptionsById } from "@/lib/override-rules";
import type { ConditionalRuleDto } from "@babackapp/private-api-client/types";
import FormTagsInput from "@babackapp/react-hook-forms-components/form-tags-input";
import { cn } from "@babackapp/ui/lib/utils";
import useTranslation from "@babackapp/translations/use-translation";
import { FormMessage } from "@babackapp/ui/components/form";
import {
  DateRangePicker,
  DateTrigger,
} from "@babackapp/ui/components/date-range-picker";

interface ValueInputProps {
  className?: string;
  value: boolean | number | number[] | string | string[];
  onChange: (value: boolean | number | number[] | string | string[]) => void;
  fieldId: string;
  operator: ConditionalRuleDto["operator"];
  placeholder?: string;
  disabled?: boolean;
}

export function ValueInput({
  className,
  value,
  onChange,
  fieldId,
  operator,
  placeholder = "Value",
  disabled,
}: ValueInputProps) {
  const t = useTranslation();
  const field = getFieldById(fieldId);
  const fieldOptions = getFieldOptionsById(fieldId);

  if (!field) {
    if (typeof value === "boolean") {
      return (
        <>
          <div className="flex items-center gap-2">
            <Switch className={className} checked={value} disabled={true} />
            <span className="text-sm">{value ? "True" : "False"}</span>
          </div>
          <FormMessage />
        </>
      );
    } else {
      return (
        <>
          <Input
            value={(value ?? "").toString()}
            placeholder={placeholder}
            disabled={true}
            className={cn("w-32 h-8", className)}
          />
          <FormMessage />
        </>
      );
    }
  }

  // Key-value array field
  if (field.type === "KEY_VALUE_ARRAY") {
    const kvValue = (value as unknown as { key: string; value: string }) || {
      key: "",
      value: "",
    };
    return (
      <>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={kvValue.key ?? ""}
            onChange={(e) =>
              onChange({
                key: e.target.value,
                value: kvValue.value,
              } as unknown as string)
            }
            placeholder={t("overrideRules.misc.key")}
            disabled={disabled}
            className={cn("w-28 h-8", className)}
          />
          <Input
            type="text"
            value={kvValue.value ?? ""}
            onChange={(e) =>
              onChange({
                key: kvValue.key,
                value: e.target.value,
              } as unknown as string)
            }
            placeholder={t("overrideRules.misc.value")}
            disabled={disabled}
            className={cn("w-28 h-8", className)}
          />
        </div>
        <FormMessage />
      </>
    );
  }

  // Boolean field
  if (field.type === "BOOLEAN") {
    return (
      <>
        <div className="flex items-center gap-2">
          <Switch
            className={className}
            checked={Boolean(value)}
            onCheckedChange={onChange}
            disabled={disabled}
          />
          <span className={cn("text-sm", className)}>
            {Boolean(value) ? "True" : "False"}
          </span>
        </div>
        <FormMessage />
      </>
    );
  }

  // Field with predefined options
  if (
    fieldOptions &&
    ((field.type === "STRING" && (operator === "EQ" || operator === "NE")) ||
      (field.type === "ARRAY" &&
        (operator === "CONTAINS" || operator === "NOT_CONTAINS")))
  ) {
    return (
      <>
        <Select
          value={value as string}
          onValueChange={onChange}
          disabled={disabled}
        >
          <SelectTrigger className={cn("w-32 h-8", className)}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {fieldOptions.options.map((option) => (
              <SelectItem key={option} value={option}>
                {t(
                  `overrideRules.fieldsOptions.${fieldOptions.id}.${option}.label`,
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FormMessage />
      </>
    );
  }

  // Multi-value input ONLY for "in" and "not_in" operators (not for "contains")
  if (operator === "IN" || operator === "NOT_IN") {
    return (
      <FormTagsInput
        className={cn(className)}
        label=""
        value={
          Array.isArray(value) && value.every((val) => typeof val === "string")
            ? value
            : []
        }
        onChange={onChange}
      ></FormTagsInput>
    );
  }
  if (field.type === "NUMBER" && operator === "BETWEEN") {
    return (
      <>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={Array.isArray(value) ? (value[0] as number) : 0}
            onChange={(e) => {
              const newValue = Array.isArray(value)
                ? [value[0] as number, value[1] as number]
                : [0, 0];
              newValue[0] = Math.max(+e.target.value, 0);
              newValue[1] = Math.max(newValue[0] + 1, newValue[1]);
              onChange(newValue);
            }}
            placeholder="Min"
            disabled={disabled}
            className="w-24 h-8"
          />
          <span className="text-sm">{t("misc.and")}</span>
          <Input
            type="number"
            value={Array.isArray(value) ? (value[1] as number) : 0}
            onChange={(e) => {
              const newValue = Array.isArray(value)
                ? [value[0] as number, value[1] as number]
                : [0, 0];
              newValue[1] = Math.max(+e.target.value, 1);
              newValue[0] = Math.min(newValue[0], newValue[1] - 1);
              onChange(newValue);
            }}
            placeholder="Max"
            disabled={disabled}
            className="w-24 h-8"
          />
        </div>
        <FormMessage />
      </>
    );
  }

  if (field.type === "DATE" && operator === "BETWEEN") {
    return (
      <>
        <DateRangePicker
          className="h-8 max-w-80"
          range={
            Array.isArray(value) && value.length === 2
              ? { from: value[0] as number, to: value[1] as number }
              : { from: Date.now(), to: Date.now() + 86400000 }
          }
          customButton={
            <DateTrigger
              className={className}
              value={value}
              placeholder={placeholder}
            />
          }
          onSelect={(date: { from?: number; to?: number }) =>
            onChange([date.from ?? 0, date.to ?? 0])
          }
          closeOnSelect={true}
        />
        <FormMessage />
      </>
    );
  }

  if (field.type === "DATE") {
    return (
      <>
        <DateRangePicker
          className="h-8 max-w-80"
          range={{ from: (value as number) || Date.now(), to: undefined }}
          canSelectRange={false}
          customButton={
            <DateTrigger
              className={className}
              value={value}
              placeholder={placeholder}
            />
          }
          onSelect={(date: { from?: number; to?: number }) =>
            onChange(date.from ?? 0)
          }
          numberOfMonths={1}
          closeOnSelect={true}
        />
        <FormMessage />
      </>
    );
  }

  // Default input for string and number fields
  return (
    <>
      <Input
        type={field.type === "NUMBER" ? "number" : "text"}
        value={
          field.type === "NUMBER" ? (value as number) : (value ?? "").toString()
        }
        onChange={(e) =>
          onChange(
            field.type === "NUMBER"
              ? Math.max(+e.target.value, 0)
              : e.target.value,
          )
        }
        placeholder={placeholder}
        disabled={disabled}
        className={cn("w-32 h-8", className)}
      />
      <FormMessage />
    </>
  );
}
